import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';

const displayText = (client) => `${client.name}${client.code ? ` (${client.code})` : ''}`;

const ClientSearchSelect = ({
  clients,
  value,
  onChange,
  placeholder = '— Search client —',
  disabled = false,
}) => {
  const wrapperRef = useRef(null);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [display, setDisplay] = useState('');

  useEffect(() => {
    const selected = clients.find(c => c._id === value);
    if (selected) {
      const text = displayText(selected);
      setDisplay(text);
      setQuery(text);
      return;
    }
    setDisplay('');
    setQuery('');
  }, [value, clients]);

  useEffect(() => {
    const onDocMouseDown = (event) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(event.target)) {
        setOpen(false);
      }
    };

    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, []);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return clients;
    return clients.filter(c =>
      c.name.toLowerCase().includes(q) ||
      (c.code && c.code.toLowerCase().includes(q))
    );
  }, [clients, query]);

  const selectClient = (client) => {
    const text = displayText(client);
    setDisplay(text);
    setQuery(text);
    setOpen(false);
    onChange(client._id);
  };

  const clearSelection = () => {
    setDisplay('');
    setQuery('');
    setOpen(false);
    onChange('');
  };

  return (
    <div ref={wrapperRef} className="relative w-full">
      <div className="flex items-center border border-gray-300 rounded-lg px-3 py-2 gap-2 focus-within:ring-2 focus-within:ring-teal-500 bg-white">
        <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
        <input
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
            if (value) onChange('');
          }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          disabled={disabled}
          className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400"
        />
        {!!value && !disabled && (
          <button
            onClick={clearSelection}
            className="text-gray-400 hover:text-gray-600"
            title="Clear selected client"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        )}
      </div>

      {open && !disabled && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
          {filtered.length === 0 ? (
            <div className="px-4 py-3 text-sm text-gray-400 text-center">No clients found</div>
          ) : (
            filtered.map(client => {
              const selected = client._id === value;
              return (
                <button
                  key={client._id}
                  onClick={() => selectClient(client)}
                  className={selected
                    ? 'w-full text-left px-4 py-2 text-sm font-medium text-teal-700 bg-teal-50'
                    : 'w-full text-left px-4 py-2 text-sm text-gray-700 cursor-pointer hover:bg-teal-50 hover:text-teal-700'}
                >
                  {displayText(client)}
                </button>
              );
            })
          )}
        </div>
      )}
    </div>
  );
};

ClientSearchSelect.propTypes = {
  clients: PropTypes.array.isRequired,
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
};

export default ClientSearchSelect;
